import React, { useEffect, useMemo, useRef } from "react";
import { isMobile } from "@scrile/tools/dist/lib/browserUtils";
import { checkNeedMirrorReflection } from "lib/liveStreamHelpers";
import { ProduceStream, ProduceUser } from "types";
import CustomScrollBar from "components/CustomScrollBar";
import MainStreamView from "./components/MainStreamView";
import ProduceStreamItem from "./components/ProduceStreamItem";
import SecondStreamView from "./components/SecondStreamView";
import "./style.scss";

interface Props {
  streams: ProduceStream[];
  me: ProduceUser | null;
  setManagedPlayer: React.Dispatch<React.SetStateAction<HTMLVideoElement | null>>;
  selectedStreamId: string | null;
  setSelectedStreamId: (streamId: string | null) => void;
  showControl?: boolean;
  large: boolean;
}

export default function StreamsLayout({
  streams,
  me,
  setSelectedStreamId,
  selectedStreamId,
  setManagedPlayer,
  showControl,
  large,
}: Props) {
  const prevState = useRef({ hasScreenStream: false, userCount: 0 });

  const selectedStream = useMemo(() => {
    return streams.find((i) => i.id === selectedStreamId) || null;
  }, [streams, selectedStreamId]);

  const secondStream = useMemo(() => {
    return streams.find((i) => i.id !== selectedStreamId) || null;
  }, [streams, selectedStreamId]);

  useEffect(() => {
    if (!selectedStream && selectedStreamId) setSelectedStreamId(null);
  }, [selectedStream, selectedStreamId, setSelectedStreamId]);

  useEffect(() => {
    if (streams.length === 1) {
      setSelectedStreamId(streams[0].id);
    } else {
      const firstScreenStream = streams.find((i) => i.isScreen);
      if (!prevState.current.hasScreenStream && firstScreenStream) {
        setSelectedStreamId(firstScreenStream.id);
      } else if (streams.length === 2 && prevState.current.userCount !== streams.length) {
        const opponentStream = streams.find((i) => i.id !== me?.user?.id);
        setSelectedStreamId(opponentStream?.id || streams[0].id);
      } else if (streams.length > 2 && prevState.current.userCount < 3) {
        setSelectedStreamId(null);
      }

      prevState.current.hasScreenStream = !!firstScreenStream;
      prevState.current.userCount = streams.length;
    }
  }, [streams, me, setSelectedStreamId]);

  const cls = ["stream-layout d-center"];
  if (large) cls.push("__large");
  if (selectedStreamId) cls.push("__focused");
  else if (streams.length < (large ? 9 : 10)) cls.push("__medium");
  else cls.push("__small");

  return streams.length < 3 ? (
    <>
      {selectedStream && (
        <MainStreamView
          className="stream-layout__main-stream"
          item={selectedStream}
          setPlayer={selectedStream.user.id !== me?.user.id && !selectedStream.isScreen ? setManagedPlayer : undefined}
          mirrorReflection={checkNeedMirrorReflection(selectedStream, me)}
          forceMute={selectedStream.id === me?.user.id || selectedStream.isScreen}
          onClick={() => setSelectedStreamId(selectedStream.id)}
          showControl={showControl}
        />
      )}

      {secondStream && (
        <SecondStreamView
          item={secondStream}
          setPlayer={secondStream.user.id !== me?.user.id && !secondStream.isScreen ? setManagedPlayer : undefined}
          mirrorReflection={checkNeedMirrorReflection(secondStream, me)}
          forceMute={secondStream.id === me?.user.id || secondStream.isScreen}
          isSpeaking={secondStream.isSpeaking}
          onClick={() => setSelectedStreamId(secondStream.id)}
        />
      )}
    </>
  ) : (
    <div className={cls.join(" ")}>
      <CustomScrollBar className="stream-layout__users-wrapper">
        <div className="stream-layout__users">
          {streams.map((stream) => (
            <ProduceStreamItem
              className="stream-layout__user"
              item={stream}
              key={stream.id}
              hide={stream.id === selectedStream?.id}
              forceMute={stream.id === me?.user.id || stream.isScreen}
              isSpeaking={stream.isSpeaking}
              setPlayer={stream.id !== me?.user.id && !stream.isScreen ? setManagedPlayer : undefined}
              onClick={() => setSelectedStreamId(stream.id)}
            />
          ))}
        </div>
      </CustomScrollBar>
      {selectedStream && (
        <ProduceStreamItem
          className="stream-layout__selected-user"
          item={selectedStream}
          key={selectedStream.id}
          forceMute
          contain
          isSpeaking={selectedStream.isSpeaking}
          onClick={() => !isMobile() && setSelectedStreamId(null)}
        />
      )}
    </div>
  );
}
